/**
 * Force the browser HTTP cache to revalidate deploy-critical shell URLs.
 * Cache Storage + SW unregister alone do not evict HTTP disk cache for
 * index.html / sw.js / manifests / runtime-config.
 *
 * Locales are bundled from src/locales (no public/locales HTTP shell) —
 * do not probe dead locale JSON URLs under public.
 */

export const CUSTOMER_HTTP_SHELL_BUST_PATHS: readonly string[] = [
  '/',
  '/index.html',
  '/sw.js',
  '/manifest.webmanifest',
  '/manifest-dev.webmanifest',
  '/runtime-config.json',
  '/runtime-config.js',
];

export type BustCustomerHttpShellCachesFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

function resolveLocationPathname(
  pathnameOverride: string | null | undefined,
): string | null {
  if (pathnameOverride !== undefined) {
    return pathnameOverride;
  }
  try {
    const pathname = globalThis.location?.pathname;
    return typeof pathname === 'string' && pathname.length > 0 ? pathname : null;
  } catch {
    return null;
  }
}

/**
 * Shell bust paths for this navigation: static deploy-critical URLs plus
 * current location.pathname when available (deep SPA document shell).
 */
export function resolveCustomerHttpShellBustPaths(
  pathnameOverride?: string | null,
): readonly string[] {
  const paths = new Set<string>(CUSTOMER_HTTP_SHELL_BUST_PATHS);
  const pathname = resolveLocationPathname(pathnameOverride);
  if (pathname != null && pathname.length > 0) {
    paths.add(pathname);
  }
  return [...paths];
}

/**
 * Best-effort cache-reload probes. Failures are ignored — navigation
 * still proceeds with _pwa cache-bust query.
 *
 * pathnameOverride: undefined uses globalThis.location.pathname when available;
 * null skips pathname (tests); string forces one path.
 */
export async function bustCustomerHttpShellCaches(
  fetchImpl: BustCustomerHttpShellCachesFetch = fetch,
  pathnameOverride?: string | null,
): Promise<void> {
  const paths = resolveCustomerHttpShellBustPaths(pathnameOverride);
  await Promise.all(
    paths.map(async (path) => {
      try {
        await fetchImpl(path, {
          method: 'GET',
          cache: 'reload',
          credentials: 'same-origin',
          headers: {
            Pragma: 'no-cache',
            'Cache-Control': 'no-cache',
          },
        });
      } catch {
        // Offline / aborted — Update still escalates to hard navigate.
      }
    }),
  );
}
